import { getAuthUser, isAuthorised } from '../../spa/store/general/selectors';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { IState, TAsyncThunk } from '../../spa/store/types';
import { API_URL } from '../constants';
import { setAuthUserAction } from '../../spa/store/general/actions';
import { AuthManager } from './auth-manager';

const AUTH_REFRESH_TOKEN_URL = API_URL + '/user/auth/refreshToken';

axios.defaults.withCredentials = true;

// There's nothing to show without a session — send the user to the call entry point. Guard
// against a redirect loop when already there. A full load, not a history push: without a session
// cookie the server answers `/` with the static guest landing.
const redirectToCall = (): void => {
  const path = window.location.pathname;
  if (path !== '/' && !path.startsWith('/call') && !path.startsWith('/admin')) {
    window.location.assign('/');
  }
}

// A 403 (or a 401 that survives a token refresh) means the session cookie (lm_id_token) is
// gone/invalid.
const redirectIfNoSession = (reason: AxiosError): void => {
  const status = reason?.response?.status;
  if (status === 403 || status === 401) {
    redirectToCall();
  }
}

// Runs `exec`, and on 401 refreshes the token once and retries. A 403, a rejected refresh
// (dead session), or a retry that still fails all reject — checking for a lost session first.
const withAuthRetry = (
  dispatch: any,
  getState: () => IState,
  exec: () => Promise<any>,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    exec()
      .then(resolve)
      .catch(async (reason: AxiosError) => {
        if (!checkErrorCode(reason, 401)) {
          redirectIfNoSession(reason);
          return reject(reason);
        }
        try {
          await refreshToken(dispatch, getState);
        } catch (e) {
          // Refresh itself failed — the session can't be renewed (dead), whatever the error shape
          // (401/403, or a soft 200-without-token that has no status). Redirect regardless.
          redirectToCall();
          return reject(e);
        }
        exec()
          .then(resolve)
          .catch((e: AxiosError) => {
            redirectIfNoSession(e);
            reject(e);
          });
      });
  });
};

export const getRequest = (
  url: string,
  params: any = {}
): TAsyncThunk => (
  dispatch,
  getState
) => {
  return withAuthRetry(dispatch, getState, () => execGetRequest(getState, url, params));
};

export const getRequestWithoutToken = async (
  url: string,
  params: any = {}
): Promise<any> => {
  const requestConfig = {
    ...params
  };
  return axios.get(url, requestConfig)
    .then((response: AxiosResponse) => {
      return response.data;
    });
}

export const postRequest = (
  url: string,
  body: any,
  params: any = {}
): TAsyncThunk => (
  dispatch,
  getState
) => {
  return withAuthRetry(dispatch, getState, () => execPostRequest(getState, url, body, params));
}

export const deleteRequest = (
  url: string,
  params: any = {}
): TAsyncThunk => (
  dispatch,
  getState
) => {
  return withAuthRetry(dispatch, getState, () => execDeleteRequest(getState, url, params));
}

export const postRequestWithoutToken = async (
  url: string,
  body: any,
  params: any = {}
): Promise<any> => {
  const requestConfig = {
    ...params
  };
  return axios.post(url, body, requestConfig)
    .then((response: AxiosResponse) => response.data);
}

const checkErrorCode = (reason: AxiosError, code: number) => {
  return reason?.response?.status === code;
}

const execGetRequest = (
  getState: () => IState,
  url: string,
  params: any = {},
): Promise<any> => {
  const state = getState();
  const authUser = getAuthUser(state);
  if (!isAuthorised(state)) {
    return Promise.reject(new Error('unauth user: ' + url));
  }

  const requestConfig = {
    headers: { authorization: authUser?.token },
    ...params
  };
  return axios.get(url, requestConfig)
    .then((response: AxiosResponse) => response.data);
}

const execPostRequest = (
  getState: () => IState,
  url: string,
  body: any,
  params: any = {}
): Promise<any> => {
  const state = getState();
  const authUser = getAuthUser(state);
  if (!isAuthorised(state)) {
    return Promise.reject(new Error('unauth user: ' + url));
  }

  const requestConfig = {
    headers: { authorization: authUser?.token },
    ...params
  };
  return axios.post(url, body, requestConfig)
    .then((response: AxiosResponse) => response.data);
}

const execDeleteRequest = (
  getState: () => IState,
  url: string,
  params: any = {}
): Promise<any> => {
  const state = getState();
  const authUser = getAuthUser(state);
  if (!isAuthorised(state)) {
    return Promise.reject(new Error('unauth user: ' + url));
  }

  const requestConfig = {
    headers: { authorization: authUser?.token },
    ...params
  };
  return axios.delete(url, requestConfig)
    .then((response: AxiosResponse) => response.data);
}

let refreshTokenLockPromise: Promise<any> | null = null;

const refreshToken = async (
  dispatch: (actions: any) => Promise<any>,
  getState: () => IState
) => {

  const authUser = getAuthUser(getState());
  if (!isAuthorised(getState())) {
    return Promise.reject(new Error('unauth user'));
  }

  if (!!refreshTokenLockPromise) {
    return refreshTokenLockPromise;
  }

  refreshTokenLockPromise = new Promise((resolve, reject) => {
    const body = { refreshToken: authUser?.refreshToken }
    axios.post(AUTH_REFRESH_TOKEN_URL, body, {})
      .then((response: AxiosResponse) => {
        const newAccessToken = response.data.accessToken;
        const authUser = getAuthUser(getState());
        // No token (or the user vanished) is as much a failure as a rejected request —
        // reject so the caller can react instead of hanging on an unsettled promise.
        if (!newAccessToken || !authUser) {
          reject(new Error('refresh failed: no token'));
          return;
        }
        dispatch(setAuthUserAction({
          ...authUser,
          ...{token: newAccessToken}
        }));
        AuthManager.updateUserToken(newAccessToken);
        resolve(0);
      })
      .catch(reject)
      // Always release the lock, otherwise one failed refresh wedges every later one.
      .finally(() => { refreshTokenLockPromise = null; });
  });

  return refreshTokenLockPromise;
}
